import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { getPostLoginPath } from '../utils/postLoginRoute';
import { resolveApiBase } from '../utils/httpClient';
import axios from 'axios';

const Shell = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1f5f9;
  padding: clamp(1rem, 4vw, 2rem);
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  padding: clamp(1.5rem, 4vw, 2.5rem);
  width: 100%;
  max-width: 460px;
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.75rem;
  color: #0f766e;
  text-align: center;
`;

const Body = styled.p`
  margin: 0 0 1.25rem;
  color: #475569;
  line-height: 1.55;
  text-align: center;
`;

const Notice = styled.div`
  background: ${(p) => (p.$error ? '#fef2f2' : '#ecfdf5')};
  border: 1px solid ${(p) => (p.$error ? '#fecaca' : '#99f6e4')};
  color: ${(p) => (p.$error ? '#b91c1c' : '#115e59')};
  padding: 0.85rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  font-weight: 600;
  text-align: center;
`;

const Field = styled.label`
  display: block;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #334155;
  input {
    display: block;
    width: 100%;
    margin-top: 0.35rem;
    padding: 0.65rem 0.75rem;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    font: inherit;
  }
`;

const Button = styled.button`
  width: 100%;
  background: #0f766e;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.65;
    cursor: wait;
  }
`;

const Links = styled.div`
  text-align: center;
  margin-top: 1.25rem;
  color: #64748b;
  a {
    color: #0f766e;
    font-weight: 600;
    text-decoration: none;
  }
`;

const VerifyEmail = () => {
  const { verifyEmail } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token: pathToken } = useParams();
  const initialToken = (pathToken || searchParams.get('token') || '').trim();

  const [token, setToken] = useState(initialToken);
  const [status, setStatus] = useState(initialToken ? 'verifying' : 'idle');
  const [message, setMessage] = useState(
    initialToken ? 'Verifying your email…' : 'Paste the token from your email, or open the link again.'
  );
  const [email, setEmail] = useState('');
  const [resendNote, setResendNote] = useState('');
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!initialToken) return undefined;
    let cancelled = false;
    (async () => {
      const result = await verifyEmail(initialToken);
      if (cancelled) return;
      if (result.success) {
        setStatus('ok');
        setMessage(result.message || 'Email verified. Signing you in…');
        navigate(getPostLoginPath(result.user), { replace: true });
      } else {
        setStatus('error');
        setMessage(result.error || 'Verification failed');
      }
    })();
    return () => {
      cancelled = true;
    };
    // verifyEmail is stable enough for one-shot token links; avoid re-verify loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialToken]);

  const onSubmitToken = async (e) => {
    e.preventDefault();
    const value = token.trim();
    if (!value) {
      setStatus('error');
      setMessage('Verification token is required');
      return;
    }
    setStatus('verifying');
    setMessage('Verifying your email…');
    const result = await verifyEmail(value);
    if (result.success) {
      setStatus('ok');
      setMessage(result.message || 'Email verified.');
      navigate(getPostLoginPath(result.user), { replace: true });
    } else {
      setStatus('error');
      setMessage(result.error || 'Verification failed');
    }
  };

  const onResend = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setResendNote('Enter the email you registered with.');
      return;
    }
    setResending(true);
    setResendNote('');
    try {
      const { data } = await axios.post(`${resolveApiBase()}/api/auth/resend-verification/`, {
        email: email.trim(),
      });
      setResendNote(data?.message || 'Verification email sent if the account exists and is unverified.');
    } catch (err) {
      setResendNote(err.response?.data?.error || 'Could not resend verification email.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Shell>
      <Card>
        <Title>Verify email</Title>
        <Body>
          New accounts stay inactive until this step succeeds. Demo logins on the sign-in page are
          already verified.
        </Body>

        {message ? <Notice $error={status === 'error'}>{message}</Notice> : null}

        {status !== 'verifying' && status !== 'ok' && (
          <form onSubmit={onSubmitToken}>
            <Field>
              Verification token
              <input
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste token from the email link"
                autoComplete="off"
              />
            </Field>
            <Button type="submit" disabled={status === 'verifying'}>
              Verify and continue
            </Button>
          </form>
        )}

        {status === 'error' && (
          <form onSubmit={onResend} style={{ marginTop: '1.5rem' }}>
            <Body style={{ textAlign: 'left', marginBottom: '0.75rem' }}>
              Need a new link? Resend to the address you used at registration.
            </Body>
            <Field>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </Field>
            <Button type="submit" disabled={resending}>
              {resending ? 'Sending…' : 'Resend verification email'}
            </Button>
            {resendNote ? <Notice style={{ marginTop: '0.75rem' }}>{resendNote}</Notice> : null}
          </form>
        )}

        <Links>
          <Link to="/login">Back to sign in</Link>
          {' · '}
          <Link to="/register">Register</Link>
        </Links>
      </Card>
    </Shell>
  );
};

export default VerifyEmail;
